import {
  Box,
  MaterialTableWrapper,
  MaterialTableWrapperProps,
  ModalContainer,
  MRT_ColumnDef,
  MRT_TableOptions,
  RowActionButton,
  useDisclosure,
  withThemeProvider,
} from '@ouster/webviz-react-components';

import { useCallback, useMemo, useState } from 'react';
import { BiDetail } from 'react-icons/bi';
import { TbCurrentLocation, TbDeviceIpadHorizontalCog } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import AdminStorageConfig from './AdminStorageConfig';
import AdminStoragePreferenceConfig from './AdminStoragePreferenceConfig';
import { AdminDashboardOrgBrief } from './types';
import { useAdminDashboardSummary } from './useAdminDashboard';
import { formatBytes } from '../../utils/misc';

const ADMIN_ORG_ID = 1;

const ThemedMaterialTableWrapper =
  withThemeProvider<MaterialTableWrapperProps<AdminDashboardOrgBrief>>(
    MaterialTableWrapper
  );

export default function AdminOverallOrgsSummary() {
  const {
    data: summaryData,
    isLoading: summaryLoading,
    isRefetching,
  } = useAdminDashboardSummary();

  const navigate = useNavigate();

  const columns = useMemo<MRT_ColumnDef<AdminDashboardOrgBrief>[]>(
    () => [
      {
        accessorKey: 'organization_name',
        header: 'Organization',
        minSize: 200,
        size: 200,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 80,
      },
      {
        accessorKey: 'created_at',
        header: 'Created at',
        size: 120,
        Cell: ({ row }) => {
          const createdAt = row.original.created_at;
          return typeof createdAt === 'string'
            ? new Date(createdAt).toISOString().split('T')[0]
            : 'Invalid Date';
        },
      },
      {
        accessorKey: 'total_users',
        header: 'Users',
        size: 80,
      },
      {
        accessorKey: 'space_used',
        header: 'Space usage',
        size: 120,
        Cell: ({ row }) => formatBytes(row.original.space_used),
      },
      {
        accessorKey: 'allowed_space',
        header: 'Storage limit',
        size: 120,
        Cell: ({ row }) => {
          if (row.original.allowed_space === -1) {
            return 'Unlimited';
          }
          return formatBytes(row.original.allowed_space);
        },
      },
      {
        accessorKey: 'upload_limit',
        header: 'Upload limit',
        size: 120,
        Cell: ({ row }) => {
          if (row.original.upload_limit === -1) {
            return 'Unlimited';
          }
          return formatBytes(row.original.upload_limit);
        },
      },
      {
        accessorKey: 'storage_preferences',
        header: 'Storage Preferences',
        size: 150,
        Cell: ({ row }) => row.original.storage_preferences.join(', '),
      },
    ],
    []
  );

  const {
    isOpen: isConfigDialogOpen,
    onOpen: onConfigDialogOpen,
    onClose: onConfigDialogClose,
  } = useDisclosure();

  const [configDialogState, setConfigDialogState] = useState<{
    type: 'storage' | 'replication' | null;
    orgId: number | undefined;
  }>({ type: null, orgId: undefined });

  const storageConfigHandler = useCallback(
    (row: AdminDashboardOrgBrief) => {
      setConfigDialogState({ type: 'storage', orgId: row.organization_id });
      onConfigDialogOpen();
    },
    [onConfigDialogOpen]
  );

  const replicationConfigHandler = useCallback(
    (row: AdminDashboardOrgBrief) => {
      setConfigDialogState({ type: 'replication', orgId: row.organization_id });
      onConfigDialogOpen();
    },
    [onConfigDialogOpen]
  );

  const organizationExploreHandler = useCallback(
    (row: AdminDashboardOrgBrief) => {
      navigate(
        `/admin_dashboard/organization_hub/org_overview/${row.organization_id}`
      );
    },
    [navigate]
  );

  const closeDialogs = useCallback(() => {
    setConfigDialogState({ type: null, orgId: undefined });
    onConfigDialogClose();
  }, [onConfigDialogClose]);

  const configOrgInfo = useMemo(
    () =>
      configDialogState.orgId
        ? summaryData?.organizations.find(
            (org) => org.organization_id === configDialogState.orgId
          )
        : undefined,
    [summaryData?.organizations, configDialogState.orgId]
  );

  const tableOptions: MRT_TableOptions<AdminDashboardOrgBrief> = useMemo(
    () => ({
      columns,
      data: summaryData?.organizations || [],
      initialState: {
        pagination: { pageSize: 50, pageIndex: 0 },
        columnPinning: { right: ['mrt-row-actions'], left: ['name'] },
        showGlobalFilter: true,
      },
      enableRowActions: true,
      positionGlobalFilter: 'left',
      enableSorting: true,
      enableToolbarInternalActions: false,
      positionActionsColumn: 'last',
      muiTableContainerProps: {
        sx: {
          maxHeight: `calc(100% - 120px)`,
        },
      },
      muiPaginationProps: {
        rowsPerPageOptions: [50, 100, 200, 300],
      },
      getRowId: (originalRow) =>
        `${originalRow.organization_id} - ${originalRow.organization_name}`,
      renderRowActions: ({ row }) => (
        <Box flexWrap="nowrap" display="flex">
          <RowActionButton<AdminDashboardOrgBrief>
            title="Configure Organization Setting"
            icon={<TbDeviceIpadHorizontalCog />}
            row={row.original}
            onClickHandler={storageConfigHandler}
            isDisabled={row.original.organization_id === ADMIN_ORG_ID}
          />
          <RowActionButton<AdminDashboardOrgBrief>
            title="Configure Replication"
            icon={<TbCurrentLocation />}
            row={row.original}
            onClickHandler={replicationConfigHandler}
          />
          <RowActionButton<AdminDashboardOrgBrief>
            title="Explore Company Details"
            icon={<BiDetail />}
            row={row.original}
            onClickHandler={organizationExploreHandler}
          />
        </Box>
      ),
      state: {
        isLoading: summaryLoading,
        showLoadingOverlay: false,
        showAlertBanner: false,
        showSkeletons: summaryLoading || isRefetching,
      },
    }),
    [
      columns,
      summaryData?.organizations,
      summaryLoading,
      isRefetching,
      storageConfigHandler,
      replicationConfigHandler,
      organizationExploreHandler,
    ]
  );

  return (
    <Box
      flexGrow={1}
      display="grid"
      gap="1rem"
      p="1rem"
      border="1px solid var(--color-unify-white-40)"
      borderRadius={8}
      height="100%"
      overflow="hidden"
      data-testid="admin-overall-orgs-box"
    >
      <ThemedMaterialTableWrapper {...tableOptions} />

      {configOrgInfo && configDialogState.type === 'storage' && (
        <ModalContainer
          isOpen={isConfigDialogOpen}
          onClose={closeDialogs}
          header={configOrgInfo.organization_name}
          content={
            <AdminStorageConfig {...configOrgInfo} handleClose={closeDialogs} />
          }
          footer={null}
        />
      )}

      {configOrgInfo && configDialogState.type === 'replication' && (
        <ModalContainer
          isOpen={isConfigDialogOpen}
          onClose={closeDialogs}
          header={configOrgInfo.organization_name}
          content={
            <AdminStoragePreferenceConfig
              {...configOrgInfo}
              handleClose={closeDialogs}
            />
          }
          footer={null}
        />
      )}
    </Box>
  );
}
